import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import AddBoxIcon from '@mui/icons-material/AddBox'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import {
  Box,
  createTheme,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Tooltip,
  useTheme,
} from '@mui/material'
import _ from 'lodash'
import PropTypes from 'prop-types'
import {
  ActionButton,
  Button,
  Dialog,
  Scrollbar,
  SubmitButton,
  Title,
} from '~/components'
import { useCanEdit } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'

const ValueTitle = (props) => {
  const { value, sx = {}, ...rest } = props
  return (
    <Tooltip title={value}>
      <Box sx={{ width: '65px', position: 'relative' }}>
        &nbsp;
        <Box
          {...rest}
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            textOverflow: 'ellipsis',
            ...sx,
          }}
        >
          {value}
        </Box>
      </Box>
    </Tooltip>
  )
}

ValueTitle.propTypes = {
  sx: PropTypes.object,
  value: PropTypes.string.isRequired,
}

const PropertyMatrix = (props) => {
  const { property_id } = props
  const { t } = useTranslation()
  const canEdit = useCanEdit()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { subCategory } = useRouteContext(PropertyMatrix.name)
  const [data, setData] = useState()
  const [products, setProducts] = useState()
  const [values, setValues] = useState()

  useEffect(() => {
    if (!property_id || !subCategory) return
    let cancelled = false
    ;(async () => {
      const { payload } = await dispatch(
        bshSlice.loadMatrix({ data: { property_id, category: subCategory } })
      )
      if (cancelled) return
      setData(payload)
      setProducts(payload?.products)
      setValues(payload?.values)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property_id, subCategory])

  const rootTheme = useTheme()
  const theme = createTheme(
    _.merge({}, rootTheme, {
      components: {
        ...rootTheme.components,
        MuiTableContainer: {
          styleOverrides: {
            root: {
              height: '100%',
            },
          },
        },
        MuiTable: {
          styleOverrides: {
            root: {
              width: 'auto',
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:nth-of-type(odd)': {
                backgroundColor:
                  rootTheme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.01)'
                    : 'rgba(255, 255, 255, 0.035)',
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: ({ ownerState }) => {
              let styles = {
                borderColor: 'transparent',
                fontWeight: ownerState.variant === 'head' ? 'bold' : undefined,
                textWrap: 'nowrap',
                '&:not(:last-child)': {
                  borderRight: `1px solid ${theme.palette.divider}`,
                },
                userSelect: 'none',
              }
              const scope = ownerState.scope?.split(' ')
              if (scope?.includes('sticky')) {
                styles = {
                  ...styles,
                  position: 'sticky',
                  left: 0,
                  zIndex:
                    rootTheme.zIndex.appBar +
                    (ownerState.variant === 'head' ? 2 : 1),
                }
              }
              if (scope?.includes('original')) {
                styles = {
                  ...styles,
                  backgroundColor:
                    rootTheme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.1)'
                      : 'rgba(255, 255, 255, 0.1)',
                  borderColor: theme.palette.divider,
                }
              }
              if (
                scope?.reduce(
                  (acc, cur) => acc || ['default', 'set'].includes(cur),
                  false
                )
              ) {
                styles = {
                  ...styles,
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '8px',
                    height: '8px',
                    borderRadius: '100%',
                    transform: 'translate(-50%, -50%)',
                    ...(scope.includes('default')
                      ? {
                          backgroundColor:
                            rootTheme.palette.mode === 'light'
                              ? '#ffffff'
                              : '#000000',
                          border: `1px solid ${rootTheme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.35)'}`,
                        }
                      : scope.includes('set')
                        ? { backgroundColor: rootTheme.palette.primary.main }
                        : { backgroundColor: 'blue' }),
                  },
                }
              }
              if (scope?.includes('empty')) {
                styles.backgroundColor = rootTheme.palette.background.default
              }
              if (scope?.includes('clickable')) {
                styles = {
                  ...styles,
                  cursor: 'pointer',
                  textAlign: 'center',
                }
              }
              return styles
            },
          },
        },
      },
    })
  )

  const [prevValue, setPrevValue] = useState()
  const [newValue, setNewValue] = useState()

  const assignValue = useCallback(
    (i, value) => {
      if (!canEdit) {
        return
      }
      setProducts((prev) => {
        const newProducts = [...prev]
        const product = newProducts[i]
        if (product.value !== value) {
          product.value = value
          product.changed = product.original_value !== value
        }
        return newProducts
      })
    },
    [canEdit]
  )

  const dragEnter = useCallback(
    (i, value, e) => {
      if (e.buttons === 1) {
        assignValue(i, value)
      }
    },
    [assignValue]
  )

  const [saving, setSaving] = useState(false)

  return (
    <>
      <Title
        actions={
          <Stack direction="row" spacing={1}>
            {canEdit && (
              <SubmitButton
                label={t('ui.save')}
                loading={saving}
                size="small"
                title={t('property.save-matrix')}
                variant="contained"
                onClick={async () => {
                  setSaving(true)
                  await dispatch(
                    bshSlice.saveMatrix({
                      data: { property_id, category: subCategory, products },
                    })
                  )
                  setSaving(false)
                }}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(location.pathname)}
            >
              {t('ui.close')}
            </Button>
          </Stack>
        }
      >
        {data?.property?.description}
      </Title>
      <Box sx={{ position: 'relative', flexGrow: 1 }}>
        <Scrollbar className="absolute inset-0">
          <ThemeProvider theme={theme}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell scope="sticky">{t('product.list')}</TableCell>
                  {values?.original?.map((value, i) => (
                    <TableCell key={i}>
                      <ValueTitle value={value} />
                    </TableCell>
                  ))}
                  {values?.changed?.map((value, i) => (
                    <TableCell key={i}>
                      <Stack sx={{ alignItems: 'center' }}>
                        <ValueTitle
                          sx={{ cursor: canEdit ? 'pointer' : undefined }}
                          value={value}
                          onClick={() => {
                            if (!canEdit) return
                            setNewValue(value)
                            setPrevValue(value)
                          }}
                        />
                        {canEdit && (
                          <Box>
                            <ActionButton
                              icon={<IndeterminateCheckBoxIcon />}
                              title={t('property.remove-value')}
                              onClick={() => {
                                setProducts((prev) => {
                                  const newProducts = [...prev]
                                  newProducts.forEach((product) => {
                                    if (product.value === value) {
                                      product.value = ''
                                    }
                                  })
                                  return newProducts
                                })
                                setValues((prev) => {
                                  const newValues = { ...prev }
                                  const pos = newValues.changed.indexOf(value)
                                  if (pos >= 0) {
                                    newValues.changed.splice(pos, 1)
                                  }
                                  return newValues
                                })
                              }}
                            />
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                  ))}
                  {canEdit && (
                    <TableCell>
                      <ActionButton
                        icon={<AddBoxIcon />}
                        title={t('property.add-new-value')}
                        onClick={() => setNewValue('')}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {products?.map((product, i) => (
                  <TableRow key={i} hover>
                    <TableCell scope="sticky empty">{product.name}</TableCell>
                    {values?.original?.map((value, j) => (
                      <TableCell
                        key={j}
                        scope={[
                          'original clickable',
                          product.value === value && !product.changed
                            ? 'set'
                            : product.original_value === value &&
                                product.changed
                              ? 'default'
                              : undefined,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onMouseDown={() => assignValue(i, value)}
                        onMouseEnter={dragEnter.bind(null, i, value)}
                      >
                        &nbsp;
                      </TableCell>
                    ))}
                    {values?.changed?.map((value, j) => (
                      <TableCell
                        key={j}
                        scope={[
                          'clickable',
                          product.value === value && product.changed
                            ? 'set'
                            : undefined,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onMouseDown={() => assignValue(i, value)}
                        onMouseEnter={dragEnter.bind(null, i, value)}
                      >
                        &nbsp;
                      </TableCell>
                    ))}
                    <TableCell scope="empty" />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ThemeProvider>
        </Scrollbar>
      </Box>
      <Dialog
        actions={[
          { id: 'confirm', name: t('ui.save'), variant: 'contained' },
          { id: 'cancel', name: t('ui.cancel'), variant: 'outlined' },
        ]}
        body={() => (
          <TextField
            fullWidth
            value={newValue ?? ''}
            onChange={(e) => setNewValue(e.target.value)}
          />
        )}
        fullWidth
        maxWidth="xs"
        open={newValue !== undefined}
        title={t('property.add-new-value')}
        onClose={(action) => {
          if (action !== 'confirm') return
          if (newValue) {
            if (prevValue && prevValue !== newValue) {
              setProducts((prev) => {
                const newProducts = [...prev]
                newProducts.forEach((product) => {
                  if (product.value === prevValue) {
                    product.value = newValue
                    // product.changed = product.original_value !== newValue
                  }
                })
                return newProducts
              })
            }
            setValues((prev) => {
              const newValues = { ...prev }
              if (prevValue && prevValue !== newValue) {
                const pos = newValues.changed.indexOf(prevValue)
                if (pos >= 0) {
                  newValues.changed.splice(pos, 1)
                }
              }
              newValues.changed.push(newValue)
              newValues.changed = _.uniq(newValues.changed)
              newValues.changed.sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' })
              )
              return newValues
            })
          }
          setNewValue()
        }}
      />
    </>
  )
}

PropertyMatrix.propTypes = {
  property_id: PropTypes.number.isRequired,
}

export default PropertyMatrix
